/**
 * The honest /s filter panel (spec docs/unified-search/spec.md §2 and §5) — which panel filters
 * our data can answer for the current listing-type selection, and under which label.
 *
 * Measured facts behind the rules (docs/unified-search/research/inventory-and-hosted-config.md,
 * 2026-10-18):
 * - Price: one slider mixed CHF/h, CHF/day, CHF/month and per-slot prices. It is only offered
 *   once exactly one booking type is chosen, and its label names the unit. A price param that
 *   is already in the URL (deep link, a shared search) keeps the filter visible so it stays
 *   removable — the duck still applies it.
 * - Seats: the hosted `seats` filter counts concurrent availability seats, not persons (31 of 33
 *   hourly rooms have 1, so "Plätze ≥ 2" removed every meeting room), and without dates the UI
 *   added a one-week range on its own. The only honest number is the public stock of monthly
 *   desks: "Freie Plätze" → `minStock`. fixdesk `minStock` 1 / 2 / 5 in the Zürich box →
 *   24 / 17 / 12 results.
 *
 * Pure functions: the server render and the first client render read the same URL and get the
 * same plan. Gated by REACT_APP_UNIFIED_SEARCH; with the flag off `panelPlan` returns today's
 * panel (nothing hidden, no label overrides).
 */

import { isUnifiedSearchEnabled } from './unifiedSearch';
import {
  availableRentalDurations,
  parseListingTypeParam,
  LISTING_TYPE_PARAM,
} from './searchQuickFilters';

/**
 * "Freie Plätze" instead of the hosted seats filter — pending the owner's OK (spec §9,
 * question 1). Set to false to get today's seats behaviour back while the rest of the honest
 * panel stays: the filter shows for every selection under its old label, a seats value without
 * dates gets the one-week range again, and the duck sends `seats` with the dates.
 */
export const HONEST_PLACES = true;

const MONTHLY = 'monthly';
const PRICE_KEY = 'price';
const SEATS_KEY = 'seats';

const PRICE_HINT_ID = 'UnifiedSearch.price.needsBooking';
const PLACES_LABEL_ID = 'UnifiedSearch.places.label';

/** @returns {boolean} whether `seats` means free monthly desks (flag on and HONEST_PLACES) */
export const isHonestPlacesActive = () => isUnifiedSearchEnabled() && HONEST_PLACES;

const lower = id => `${id}`.toLowerCase();

/**
 * The selected listing type ids. On /s/:listingType the path pins the type — the duck overrides
 * any `pub_listingType` query param with it, so the plan does the same.
 */
const selectedIdsOf = (urlQueryParams = {}, listingTypePathParam) =>
  listingTypePathParam
    ? [`${listingTypePathParam}`]
    : parseListingTypeParam(urlQueryParams?.[LISTING_TYPE_PARAM]);

/**
 * Which booking types (rental durations) a list of listing type ids selects.
 *
 * `singleDuration` is set only when every selected id belongs to that one duration — a
 * selection that also carries an id outside it (a lounge, an unknown type) mixes units.
 * Ids are compared case-insensitively (the hosted ids mix casing, a path may not).
 *
 * @param {Array<Object>} listingTypes `config.listing.listingTypes`
 * @param {Array<string>} ids selected listing type ids
 * @returns {{ selectedDurations: Array<string>, singleDuration: string|null }}
 */
export const durationsForIds = (listingTypes = [], ids = []) => {
  const selectedLower = ids.map(lower);
  const durations = availableRentalDurations(listingTypes).map(d => ({
    key: d.key,
    ids: d.listingTypeIds.map(lower),
  }));
  const selected = durations.filter(d => d.ids.some(id => selectedLower.includes(id)));
  const only = selected.length === 1 ? selected[0] : null;
  const singleDuration = only && selectedLower.every(id => only.ids.includes(id)) ? only.key : null;
  return { selectedDurations: selected.map(d => d.key), singleDuration };
};

/**
 * The plan for the /s filter panel.
 *
 * @param {Object} p
 * @param {Object} [p.urlQueryParams] the panel's params (URL, or URL + staged values)
 * @param {Array<Object>} [p.listingTypes] `config.listing.listingTypes`
 * @param {string} [p.listingTypePathParam] `:listingType` of /s/:listingType
 * @returns {{
 *   selectedDurations: Array<string>,
 *   singleDuration: string|null,
 *   showPrice: boolean,
 *   priceLabelId: string|null,
 *   priceHintId: string|null,
 *   showPlaces: boolean,
 *   placesLabelId: string|null,
 *   hiddenFilterKeys: Array<string>,
 * }}
 *   `priceHintId` is set whenever no single booking type is chosen (the panel shows it in place
 *   of a hidden price filter); `hiddenFilterKeys` are the built-in filter keys to leave out of
 *   the panel.
 */
export const panelPlan = ({
  urlQueryParams = {},
  listingTypes = [],
  listingTypePathParam,
} = {}) => {
  const ids = selectedIdsOf(urlQueryParams, listingTypePathParam);
  const { selectedDurations, singleDuration } = durationsForIds(listingTypes, ids);

  if (!isUnifiedSearchEnabled()) {
    return {
      selectedDurations,
      singleDuration,
      showPrice: true,
      priceLabelId: null,
      priceHintId: null,
      showPlaces: true,
      placesLabelId: null,
      hiddenFilterKeys: [],
    };
  }

  const hasPriceParam = !!urlQueryParams?.[PRICE_KEY];
  const showPrice = !!singleDuration || hasPriceParam;
  const showPlaces = HONEST_PLACES ? singleDuration === MONTHLY : true;

  return {
    selectedDurations,
    singleDuration,
    showPrice,
    priceLabelId: singleDuration ? `UnifiedSearch.price.label.${singleDuration}` : null,
    priceHintId: singleDuration ? null : PRICE_HINT_ID,
    showPlaces,
    placesLabelId: HONEST_PLACES ? PLACES_LABEL_ID : null,
    hiddenFilterKeys: [...(showPrice ? [] : [PRICE_KEY]), ...(showPlaces ? [] : [SEATS_KEY])],
  };
};

/**
 * @param {Object} plan from panelPlan
 * @returns {Array<string>} built-in filter keys the panel leaves out
 */
export const filterKeysToOmit = plan => plan?.hiddenFilterKeys || [];

/**
 * Whether the duck turns `seats` into `minStock` for this search: honest places are on and the
 * listing-type selection is exactly the monthly booking type.
 *
 * @param {Object} p same input as panelPlan
 * @returns {boolean}
 */
export const placesApply = ({
  urlQueryParams = {},
  listingTypes = [],
  listingTypePathParam,
} = {}) =>
  isHonestPlacesActive() &&
  durationsForIds(listingTypes, selectedIdsOf(urlQueryParams, listingTypePathParam))
    .singleDuration === MONTHLY;

/**
 * @param {string|number} seats raw `seats` param
 * @returns {number|null} the requested number of free places, or null when it is not a count
 */
export const placesCount = seats => {
  const value = Number.parseInt(`${seats ?? ''}`, 10);
  return Number.isInteger(value) && value >= 1 ? value : null;
};

/**
 * Params that go along with a booking-type change in the panel (flag on), so no filter keeps a
 * meaning the new selection no longer has:
 * - `price: null` when the price unit changes (one single booking type → another, or → several):
 *   "0–50" per hour is not "0–50" per month.
 * - `seats: null` (honest places only) when the selection stops being exactly the monthly type:
 *   free places only exist for monthly desks, and a hidden filter must not keep working.
 *
 * @param {Object} p
 * @param {Array<Object>} p.listingTypes `config.listing.listingTypes`
 * @param {Array<string>} p.prevIds listing type ids before the change
 * @param {string|null} p.nextValue new `pub_listingType` value
 * @returns {Object} extra params ({} with the flag off)
 */
export const durationChangeParams = ({ listingTypes = [], prevIds = [], nextValue } = {}) => {
  if (!isUnifiedSearchEnabled()) {
    return {};
  }
  const prev = durationsForIds(listingTypes, prevIds).singleDuration;
  const next = durationsForIds(listingTypes, parseListingTypeParam(nextValue)).singleDuration;
  const priceMaybe = prev && prev !== next ? { [PRICE_KEY]: null } : {};
  const seatsMaybe =
    HONEST_PLACES && prev === MONTHLY && next !== MONTHLY ? { [SEATS_KEY]: null } : {};
  return { ...priceMaybe, ...seatsMaybe };
};
